import { useEffect } from 'react';
import { useShadowRootElements } from '@backstage/plugin-techdocs-react';
import { useThemeMode } from '../../theme/mode';

const SELECTORS = ['pre.mermaid'];

let renderCount = 0;

/**
 * Replaces the source of every `pre.mermaid` with the diagram it describes,
 * in the palette of the current theme.
 *
 * The block keeps its source in `data-mermaid-source` and the theme it was last
 * drawn in in `data-mermaid-theme`, and only blocks whose theme differs are
 * drawn. That marker is what stops a loop: `useShadowRootElements` returns a new
 * array on every render and re-renders on every mutation of the shadow root —
 * including the ones made here — so the effect cannot key on the array.
 */
export const MermaidAddon = () => {
  const blocks = useShadowRootElements<HTMLPreElement>(SELECTORS);
  const theme = useThemeMode() === 'dark' ? 'dark' : 'default';

  useEffect(() => {
    const pending = blocks.filter(pre => pre.dataset.mermaidTheme !== theme);
    if (pending.length === 0) {
      return;
    }
    for (const pre of pending) {
      pre.dataset.mermaidSource ??= pre.textContent ?? '';
      // Claimed before the await, so a render that lands in between does not
      // draw the same block twice.
      pre.dataset.mermaidTheme = theme;
    }

    (async () => {
      // Loaded on the first diagram, not with the app: mermaid is the size of
      // a small app itself, and most pages have no diagram at all.
      const { default: mermaid } = await import('mermaid');
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme });
      for (const pre of pending) {
        if (pre.dataset.mermaidTheme !== theme) {
          continue; // the theme moved on while this one was loading
        }
        try {
          const { svg } = await mermaid.render(
            `techdocs-mermaid-${++renderCount}`,
            pre.dataset.mermaidSource ?? '',
          );
          pre.innerHTML = svg;
        } catch (e) {
          // A broken diagram stays readable as its source; the reason goes to
          // the console, where the author of the page will look for it.
          // eslint-disable-next-line no-console
          console.warn('techdocs mermaid: diagram not rendered', e);
        }
      }
    })();
  });

  return null;
};
